import React, { useEffect, useState } from 'react';
import noop from 'lodash/noop';
import { Box, Stack } from '@atlaskit/primitives/compiled';

import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Spinner from '@atlaskit/spinner';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import type { ApplicationLinkManifest, ApplicationLinkV3Data } from '../../common/entities.tsx';
import { fetchApplinkManifestForUrl, fetchThisManifest } from '../../rest/index.tsx';
import {
	STATUS_RECIPROCAL_INPROGRESS,
	STATUS_RECIPROCAL_SUCCESS,
	parseCreationStateFromUrl,
	type CreationState,
} from '../../common/interchange.tsx';
import ApplinksTable from './applinks-table/index.tsx';
import ApplinksHeader from './applinks-header/index.tsx';
import { CreateFlowDialog } from './creation-flow-dialog/index.tsx';
import ReviewSubpage from './creation-flow-dialog/review/index.tsx';

const styles = cssMap({
	applinksPageContents: {
		maxWidth: '1200px',
		margin: '0 auto',
		paddingTop: token('space.400'),
		paddingRight: token('space.400'),
		paddingBottom: token('space.400'),
		paddingLeft: token('space.400'),
	},
});

type Operation =
	| 'operation-list-applinks'
	| 'operation-oauth1-reciprocal'
	| 'operation-oauth1-complete-link';

const getOperationFromCreationState = (creationState: CreationState | null): Operation => {
	if (!creationState) {
		return 'operation-list-applinks';
	}

	const statusLog = creationState.applinkCreationStatusLog;
	if (statusLog.filter((entry) => entry.status === STATUS_RECIPROCAL_SUCCESS).length > 0) {
		return 'operation-oauth1-complete-link';
	}
	if (statusLog.filter((entry) => entry.status === STATUS_RECIPROCAL_INPROGRESS).length > 0) {
		return 'operation-oauth1-reciprocal';
	}

	return 'operation-list-applinks';
};

const ApplinksPageContents = (props: { applinks: ApplicationLinkV3Data[] | null }) => {
	const [createLinkDrawerOpen, setCreateLinkDrawerOpen] = useState<boolean>(false);

	const [creationState] = useState<CreationState | null>(() => {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		const url = new URL(window.location.href);
		return parseCreationStateFromUrl(url);
	});

	const [operation, setOperation] = useState<Operation>('operation-list-applinks');

	const [thisApplicationManifest, setThisApplicationManifest] =
		useState<ApplicationLinkManifest | null>(null);
	const [targetApplicationManifest, setTargetApplicationManifest] =
		useState<ApplicationLinkManifest | null>(null);

	useEffect(() => {
		const op = getOperationFromCreationState(creationState);
		setOperation(op);
	}, [creationState]);

	useEffect(() => {
		if (creationState) {
			// We expect to always have an applinkStartingUrl in the creation state for reciprocal review
			if (operation === 'operation-oauth1-reciprocal' && creationState.applinkStartingUrl) {
				fetchApplinkManifestForUrl(creationState.applinkStartingUrl).then((remoteManifest) => {
					setTargetApplicationManifest(remoteManifest);
				});

				fetchThisManifest().then((localManifest) => {
					setThisApplicationManifest(localManifest);
				});
			}

			if (operation === 'operation-oauth1-complete-link') {
				setCreateLinkDrawerOpen(true);
			}
		}
	}, [creationState, operation]);

	const [filter, setFilter] = useState<string | null>(null);
	const [search, setSearch] = useState<string>('');

	const closeDrawer = () => {
		setCreateLinkDrawerOpen(false);
	};

	return (
		<>
			<JSErrorBoundary
				id="applinks-administration-list-page"
				teamName="olympus"
				packageName="applinksAdministrationListPage"
			>
				<Box xcss={styles.applinksPageContents}>
					{operation === 'operation-list-applinks' && (
						<Stack space="space.400">
							<ApplinksHeader
								onSearch={setSearch}
								onFilterChange={setFilter}
								onCreateLink={() => setCreateLinkDrawerOpen(true)}
								filter={filter}
								search={search}
							/>
							<Stack space="space.800">
								<ApplinksTable applinks={props.applinks} filter={filter} search={search} />
								<CreateFlowDialog isOpen={createLinkDrawerOpen} onExit={closeDrawer} />
							</Stack>
						</Stack>
					)}
					{operation === 'operation-oauth1-reciprocal' && (
						<Stack>
							{(!thisApplicationManifest || !targetApplicationManifest) && (
								<Spinner interactionName="load" label="Loading" />
							)}
							{thisApplicationManifest && targetApplicationManifest && creationState && (
								<ReviewSubpage
									state={{
										kind: 'reciprocal-review',
										variant: 'OAUTH1.0',
										localManifest: thisApplicationManifest,
										remoteManifest: targetApplicationManifest,
										inboundCreationState: creationState,
									}}
									onSuccess={noop}
									onCancel={closeDrawer} // TODO: Reciprocal review should be inside the drawer
									// TODO: Handle error
									onError={noop}
								/>
							)}
						</Stack>
					)}
					{operation === 'operation-oauth1-complete-link' &&
						creationState?.applinkOriginalCreatedId && (
							<CreateFlowDialog
								isOpen={createLinkDrawerOpen}
								onExit={closeDrawer}
								startingStage={{
									kind: 'confirmation',
									variant: 'OAUTH1.0',
									confirmationApplinkId: creationState?.applinkOriginalCreatedId,
								}}
							/>
						)}
				</Box>
			</JSErrorBoundary>
		</>
	);
};

export default ApplinksPageContents;
